import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { X, Check, Loader2, QrCode, Sparkles, AlertCircle } from "lucide-react";
import { confirmUpiQrPaymentAPI } from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { showSuccessToast, showErrorToast } from "../../Utlis/Toast";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../../../organisms/Dashboard/quotationMasterListShared";
import {
  Field,
  Label,
  Input,
  ErrorSpan,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const InfoBox = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  color: #475569;

  strong {
    color: #0f172a;
  }
`;

interface ConfirmUpiModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  paymentId: number | string;
  amount: number | string;
  customerName?: string;
  referenceId?: string;
}

export default function ConfirmUpiModal({
  isOpen,
  onClose,
  onSuccess,
  paymentId,
  amount,
  customerName,
  referenceId,
}: ConfirmUpiModalProps) {
  const [utr, setUtr] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const cleanUtr = utr.trim().replace(/\s+/g, "");
    if (!cleanUtr) {
      setError("Please enter the 12-digit UTR / UPI reference number.");
      return;
    }
    if (cleanUtr.length < 12 || cleanUtr.length > 35) {
      setError("UTR number must be at least 12 digits.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await confirmUpiQrPaymentAPI(paymentId, {
        status: "paid",
        utr: cleanUtr,
      });

      if (res && res.status === "paid") {
        showSuccessToast("Payment confirmed as PAID!");
        onSuccess?.();
        onClose();
      } else if (res?.error) {
        setError(res.error);
      }
    } catch {
      setError("Could not confirm payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Reconciliation
                </HeroBadge>
                <ModalTitle>Confirm UPI Payment</ModalTitle>
                <ModalSubtitle>
                  Verify credit in your bank app and enter customer's UTR.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            {error && <ErrorSpan style={{ display: "block" }}>{error}</ErrorSpan>}

            <FormCard>
              <InfoBox>
                <div>
                  Customer: <strong>{customerName || "Customer"}</strong>
                </div>
                <div>
                  Amount: <strong>₹{Number(amount).toFixed(2)}</strong>
                </div>
                {referenceId && (
                  <div>
                    Ref ID: <strong>{referenceId}</strong>
                  </div>
                )}
              </InfoBox>

              <Field>
                <Label htmlFor="utrInput">
                  <FormLabelIcon icon={QrCode} />
                  12-digit UPI Reference / UTR
                </Label>
                <Input
                  id="utrInput"
                  placeholder="e.g. 412356789012"
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  disabled={loading}
                />
                {utr.trim().length > 0 && utr.trim().replace(/\s+/g, "").length < 12 && (
                  <span style={{ fontSize: 11, color: "#e11d48", marginTop: 4, display: "block", fontWeight: 500 }}>
                    UTR number must be at least 12 digits ({utr.trim().replace(/\s+/g, "").length}/12)
                  </span>
                )}
              </Field>
            </FormCard>
          </ModalBody>

          <ModalActions>
            <SecondaryButton type="button" onClick={onClose} disabled={loading}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handleConfirm}
              disabled={loading || utr.trim().replace(/\s+/g, "").length < 12}
            >
              {loading ? <Spinner size={16} /> : <Check size={16} />}
              {loading ? "Confirming..." : "Confirm as Paid"}
            </PrimaryButton>
          </ModalActions>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
}
